import {useMemo, useState} from "react";
import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";
import {pluralize, priceM2Format} from "@pg-mono/string-utils";

import {PricesInsightBox} from "../../atoms/PricesInsightBox";
import {getRoomsFlatsLabel, getRoomsFlatsLabelOf} from "../../constants/prices_address_rooms";
import {ALL_ROOMS, type RoomsFilter} from "../../constants/rcn_rooms_filter";
import {IRcnHistory, IRcnHistoryPoint, useRcnHistory} from "../../hooks/use_rcn_history";
import {PRICES_ADDRESS_ANALYTICS_LOCATION} from "../../tracking/prices_city_analytics";
import type {IPricesAddress} from "../../types/IPricesAddress";
import {formatRcnHistoryPeriodAlone, formatRcnHistoryPeriodInRange} from "../../utils/format_rcn_history_period";
import {getSeriesTrend, ISeriesTrend} from "../../utils/get_series_trend";
import {PricesCityViewSectionTracker} from "../PricesCityViewSectionTracker";
import {PricesAddressTrendChart} from "./PricesAddressTrendChart";
import {PricesAddressTrendLegend} from "./PricesAddressTrendLegend";

interface Props {
    address: IPricesAddress;
    initialRooms: RoomsFilter;
    availableRooms: RoomsFilter[];
}

interface IInsightParams {
    trend: ISeriesTrend | null;
    points: IRcnHistoryPoint[];
    rooms: RoomsFilter;
    regionName: string;
    transactionsTotal: number;
}

/** What the section shows: the history of `rooms` as it was when its requests last settled. */
interface ISettledHistory extends Omit<IRcnHistory, "isLoading"> {
    rooms: RoomsFilter;
}

const toSettledHistory = ({isLoading: _isLoading, ...history}: IRcnHistory, rooms: RoomsFilter): ISettledHistory => ({
    ...history,
    rooms
});

/**
 * The last history whose requests settled. While another rooms filter loads, the insight keeps the previous text
 * instead of flashing "too few periods" for the pending series; the chart shows its loader.
 */
const useSettledHistory = (history: IRcnHistory, rooms: RoomsFilter): ISettledHistory | null => {
    // the server's render and the hydrating one settle at once - the prefetch left the history in the cache
    const [settled, setSettled] = useState<ISettledHistory | null>(() => (history.isLoading ? null : toSettledHistory(history, rooms)));
    const isUpToDate = settled?.rooms === rooms && settled.points === history.points && settled.isError === history.isError;

    if (history.isLoading || isUpToDate) {
        return settled;
    }

    const next = toSettledHistory(history, rooms);
    // storing information from this render - React re-renders at once, before anything is committed
    setSettled(next);

    return next;
};

export const PricesAddressTrend = (props: Props) => {
    const {address} = props;

    // Rooms filtering hidden for now, may return later.
    const rooms: RoomsFilter = ALL_ROOMS;

    const history = useRcnHistory(address.rcnAddress, rooms);
    const settled = useSettledHistory(history, rooms);

    const points = useMemo(() => settled?.points ?? [], [settled]);
    const trend = useMemo(() => getSeriesTrend(points), [points]);
    // Not a single year with three deeds - a guard only: the registry lists an address only with a published year.
    const hasNoPeriods = settled !== null && !settled.isError && points.length === 0 && !history.isLoading;

    return (
        <div className={sectionWrapper}>
            <Container className={containerStyle}>
                <PricesCityViewSectionTracker sectionName="address_trend" location={PRICES_ADDRESS_ANALYTICS_LOCATION} />

                <div className={cardStyle}>
                    <Text variant="headline_3" as="h2">
                        Jak zmieniały się ceny w tym budynku
                    </Text>

                    <Text variant="info_txt_1" as="p" color="var(--colors-gray-700)">
                        {getDescriptionText(address.name)}
                    </Text>

                    {/* Rooms filtering hidden for now, may return later.
                    <PricesAddressRoomsTabs label="Rozmiar mieszkań na wykresie" rooms={rooms} availableRooms={availableRooms} onRoomsChange={setRooms} /> */}

                    {hasNoPeriods ? (
                        <Text variant="body_copy_1" as="p" className={emptyStyle}>
                            {getEmptyText(rooms)}
                        </Text>
                    ) : (
                        <>
                            <PricesAddressTrendChart points={points} trend={trend} isLoading={history.isLoading} />

                            <PricesAddressTrendLegend rooms={rooms} />
                        </>
                    )}
                </div>

                {settled && !hasNoPeriods && (
                    <PricesInsightBox>
                        {settled.isError
                            ? getErrorText(settled.rooms, address.name)
                            : getInsightText({
                                  trend,
                                  points,
                                  rooms: settled.rooms,
                                  regionName: address.name,
                                  transactionsTotal: settled.transactionsTotal
                              })}
                    </PricesInsightBox>
                )}
            </Container>
        </div>
    );
};

const transactionsAccusativePluralize = pluralize(["transakcję", "transakcje", "transakcji"]);

// Polish grammar forms of "rok" (year) - the chart's only grain.
const YEAR_WORDS = {
    /** "Roczne" - the adjective opening the description */
    adjective: "Roczne",
    /** "Rok" - the subject of "pojawia się na wykresie" */
    nominative: "Rok",
    /** "roku" - "żadnego roku" */
    genitive: "roku",
    /** "lat" - "Za mało opublikowanych lat" */
    genitivePlural: "lat",
    /** "Jedyny opublikowany rok" */
    onlyOne: "Jedyny opublikowany rok",
    /** "w 3 latach" */
    locative: pluralize(["roku", "latach", "latach"])
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const formatSignedPercent = (value: number) => `${value > 0 ? "+" : ""}${value.toString().replace(".", ",")}%`;

const getDescriptionText = (addressName: string): string =>
    `${YEAR_WORDS.adjective} ceny transakcyjne z aktów notarialnych dla budynku ${addressName} – rynek pierwotny i wtórny razem. ${YEAR_WORDS.nominative} pojawia się na wykresie, gdy rejestr opublikował w nim co najmniej trzy transakcje.`;

const getEmptyText = (rooms: RoomsFilter): string =>
    `Rejestr nie opublikował jeszcze dla ${getRoomsFlatsLabelOf(rooms)} w tym budynku żadnego ${YEAR_WORDS.genitive} z co najmniej trzema transakcjami, więc nie ma historii cen do pokazania.`;

const getErrorText = (rooms: RoomsFilter, regionName: string): string =>
    `Nie udało się pobrać historii cen ${getRoomsFlatsLabelOf(rooms)} pod adresem ${regionName}. Spróbuj ponownie później.`;

const getInsightText = (params: IInsightParams): string => {
    const {trend, points, rooms, regionName, transactionsTotal} = params;

    if (!trend) {
        const onlyPoint = points.length === 1 ? points[0] : null;
        const salesPart = onlyPoint
            ? ` ${YEAR_WORDS.onlyOne}, ${formatRcnHistoryPeriodAlone(onlyPoint.date, "year")}, obejmuje ${onlyPoint.transactions} ${transactionsAccusativePluralize(onlyPoint.transactions)}.`
            : "";

        return `Za mało opublikowanych ${YEAR_WORDS.genitivePlural}, żeby wyznaczyć trend dla ${getRoomsFlatsLabelOf(rooms)} pod adresem ${regionName}.${salesPart}`;
    }

    const pointsCount = points.length;
    const range = `od ${formatRcnHistoryPeriodInRange(trend.firstDate, "year")} do ${formatRcnHistoryPeriodInRange(trend.lastDate, "year")}`;
    const coverage = `${capitalize(getRoomsFlatsLabel(rooms))} pod adresem ${regionName} sprzedano ${transactionsTotal} razy w ${pointsCount} ${YEAR_WORDS.locative(pointsCount)}, ${range}.`;
    const startPrice = priceM2Format(Math.round(trend.startValue), {precision: 0});
    const endPrice = priceM2Format(Math.round(trend.endValue), {precision: 0});

    if (trend.changePct === 0) {
        return `${coverage} Linia trendu utrzymuje się na poziomie około ${startPrice}.`;
    }

    const direction = trend.changePct > 0 ? "podnosi się" : "opada";

    return `${coverage} Linia trendu ${direction} z około ${startPrice} do ${endPrice}, czyli o ${formatSignedPercent(trend.changePct)}.`;
};

const sectionWrapper = css({
    w: "100%",
    py: 3,
    backgroundColor: "var(--colors-beige-subtle)",

    md: {
        py: 8
    }
});

const containerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    maxWidth: "1168px"
});

const cardStyle = css({
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--colors-white)",
    gap: 1.5,
    borderRadius: 3,
    boxShadow: "0 8px 20px 0 rgba(174, 177, 180, 0.25)",
    p: 2,

    md: {
        p: 4
    }
});

const emptyStyle = css({
    py: 4,
    color: "var(--colors-gray-700)"
});
import {useMemo, useState} from "react";
import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";
import {pluralize, priceM2Format} from "@pg-mono/string-utils";

import {PricesInsightBox} from "../../atoms/PricesInsightBox";
import {getRoomsFlatsLabel, getRoomsFlatsLabelOf} from "../../constants/prices_address_rooms";
import {ALL_ROOMS, type RoomsFilter} from "../../constants/rcn_rooms_filter";
import {IRcnHistory, IRcnHistoryPoint, useRcnHistory} from "../../hooks/use_rcn_history";
import {PRICES_ADDRESS_ANALYTICS_LOCATION} from "../../tracking/prices_city_analytics";
import type {IPricesAddress} from "../../types/IPricesAddress";
import {formatRcnHistoryPeriodAlone, formatRcnHistoryPeriodInRange} from "../../utils/format_rcn_history_period";
import {getSeriesTrend, ISeriesTrend} from "../../utils/get_series_trend";
import {PricesCityViewSectionTracker} from "../PricesCityViewSectionTracker";
import {PricesAddressTrendChart} from "./PricesAddressTrendChart";
import {PricesAddressTrendLegend} from "./PricesAddressTrendLegend";

interface Props {
    address: IPricesAddress;
    initialRooms: RoomsFilter;
    availableRooms: RoomsFilter[];
}

interface IInsightParams {
    trend: ISeriesTrend | null;
    points: IRcnHistoryPoint[];
    rooms: RoomsFilter;
    regionName: string;
    transactionsTotal: number;
}

/** What the section shows: the history of `rooms` as it was when its requests last settled. */
interface ISettledHistory extends Omit<IRcnHistory, "isLoading"> {
    rooms: RoomsFilter;
}

const toSettledHistory = ({isLoading: _isLoading, ...history}: IRcnHistory, rooms: RoomsFilter): ISettledHistory => ({
    ...history,
    rooms
});

/**
 * The last history whose requests settled. While another rooms filter loads, the insight keeps the previous text
 * instead of flashing "too few periods" for the pending series; the chart shows its loader.
 */
const useSettledHistory = (history: IRcnHistory, rooms: RoomsFilter): ISettledHistory | null => {
    // the server's render and the hydrating one settle at once - the prefetch left the history in the cache
    const [settled, setSettled] = useState<ISettledHistory | null>(() => (history.isLoading ? null : toSettledHistory(history, rooms)));
    const isUpToDate = settled?.rooms === rooms && settled.points === history.points && settled.isError === history.isError;

    if (history.isLoading || isUpToDate) {
        return settled;
    }

    const next = toSettledHistory(history, rooms);
    // storing information from this render - React re-renders at once, before anything is committed
    setSettled(next);

    return next;
};

export const PricesAddressTrend = (props: Props) => {
    const {address} = props;

    // Rooms filtering hidden for now, may return later.
    const rooms: RoomsFilter = ALL_ROOMS;

    const history = useRcnHistory(address.rcnAddress, rooms);
    const settled = useSettledHistory(history, rooms);

    const points = useMemo(() => settled?.points ?? [], [settled]);
    const trend = useMemo(() => getSeriesTrend(points), [points]);
    // Not a single year with three deeds - a guard only: the registry lists an address only with a published year.
    const hasNoPeriods = settled !== null && !settled.isError && points.length === 0 && !history.isLoading;

    return (
        <div className={sectionWrapper}>
            <Container className={containerStyle}>
                <PricesCityViewSectionTracker sectionName="address_trend" location={PRICES_ADDRESS_ANALYTICS_LOCATION} />

                <div className={cardStyle}>
                    <Text variant="headline_3" as="h2">
                        Jak zmieniały się ceny w tym budynku
                    </Text>

                    <Text variant="info_txt_1" as="p" color="var(--colors-gray-700)">
                        {getDescriptionText(address.name)}
                    </Text>

                    {/* Rooms filtering hidden for now, may return later.
                    <PricesAddressRoomsTabs label="Rozmiar mieszkań na wykresie" rooms={rooms} availableRooms={availableRooms} onRoomsChange={setRooms} /> */}

                    {hasNoPeriods ? (
                        <Text variant="body_copy_1" as="p" className={emptyStyle}>
                            {getEmptyText(rooms)}
                        </Text>
                    ) : (
                        <>
                            <PricesAddressTrendChart points={points} trend={trend} isLoading={history.isLoading} />

                            <PricesAddressTrendLegend rooms={rooms} />
                        </>
                    )}
                </div>

                {settled && !hasNoPeriods && (
                    <PricesInsightBox>
                        {settled.isError
                            ? getErrorText(settled.rooms, address.name)
                            : getInsightText({
                                  trend,
                                  points,
                                  rooms: settled.rooms,
                                  regionName: address.name,
                                  transactionsTotal: settled.transactionsTotal
                              })}
                    </PricesInsightBox>
                )}
            </Container>
        </div>
    );
};

const transactionsAccusativePluralize = pluralize(["transakcję", "transakcje", "transakcji"]);

// Polish grammar forms of "rok" (year) - the chart's only grain.
const YEAR_WORDS = {
    /** "Roczne" - the adjective opening the description */
    adjective: "Roczne",
    /** "Rok" - the subject of "pojawia się na wykresie" */
    nominative: "Rok",
    /** "roku" - "żadnego roku" */
    genitive: "roku",
    /** "lat" - "Za mało opublikowanych lat" */
    genitivePlural: "lat",
    /** "Jedyny opublikowany rok" */
    onlyOne: "Jedyny opublikowany rok",
    /** "w 3 latach" */
    locative: pluralize(["roku", "latach", "latach"])
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const formatSignedPercent = (value: number) => `${value > 0 ? "+" : ""}${value.toString().replace(".", ",")}%`;

const getDescriptionText = (addressName: string): string =>
    `${YEAR_WORDS.adjective} ceny transakcyjne z aktów notarialnych dla budynku ${addressName} – rynek pierwotny i wtórny razem. ${YEAR_WORDS.nominative} pojawia się na wykresie, gdy rejestr opublikował w nim co najmniej trzy transakcje.`;

const getEmptyText = (rooms: RoomsFilter): string =>
    `Rejestr nie opublikował jeszcze dla ${getRoomsFlatsLabelOf(rooms)} w tym budynku żadnego ${YEAR_WORDS.genitive} z co najmniej trzema transakcjami, więc nie ma historii cen do pokazania.`;

const getErrorText = (rooms: RoomsFilter, regionName: string): string =>
    `Nie udało się pobrać historii cen ${getRoomsFlatsLabelOf(rooms)} pod adresem ${regionName}. Spróbuj ponownie później.`;

const getInsightText = (params: IInsightParams): string => {
    const {trend, points, rooms, regionName, transactionsTotal} = params;

    if (!trend) {
        const onlyPoint = points.length === 1 ? points[0] : null;
        const salesPart = onlyPoint
            ? ` ${YEAR_WORDS.onlyOne}, ${formatRcnHistoryPeriodAlone(onlyPoint.date, "year")}, obejmuje ${onlyPoint.transactions} ${transactionsAccusativePluralize(onlyPoint.transactions)}.`
            : "";

        return `Za mało opublikowanych ${YEAR_WORDS.genitivePlural}, żeby wyznaczyć trend dla ${getRoomsFlatsLabelOf(rooms)} pod adresem ${regionName}.${salesPart}`;
    }

    const pointsCount = points.length;
    const range = `od ${formatRcnHistoryPeriodInRange(trend.firstDate, "year")} do ${formatRcnHistoryPeriodInRange(trend.lastDate, "year")}`;
    const coverage = `${capitalize(getRoomsFlatsLabel(rooms))} pod adresem ${regionName} sprzedano ${transactionsTotal} razy w ${pointsCount} ${YEAR_WORDS.locative(pointsCount)}, ${range}.`;
    const startPrice = priceM2Format(Math.round(trend.startValue), {precision: 0});
    const endPrice = priceM2Format(Math.round(trend.endValue), {precision: 0});

    if (trend.changePct === 0) {
        return `${coverage} Linia trendu utrzymuje się na poziomie około ${startPrice}.`;
    }

    const direction = trend.changePct > 0 ? "podnosi się" : "opada";

    return `${coverage} Linia trendu ${direction} z około ${startPrice} do ${endPrice}, czyli o ${formatSignedPercent(trend.changePct)}.`;
};

const sectionWrapper = css({
    w: "100%",
    py: 3,
    backgroundColor: "var(--colors-beige-subtle)",

    md: {
        py: 8
    }
});

const containerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    maxWidth: "1168px"
});

const cardStyle = css({
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--colors-white)",
    gap: 1.5,
    borderRadius: 3,
    boxShadow: "0 8px 20px 0 rgba(174, 177, 180, 0.25)",
    p: 2,

    md: {
        p: 4
    }
});

const emptyStyle = css({
    py: 4,
    color: "var(--colors-gray-700)"
});
